import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'a180-calendly',
  templateUrl: './calendly.component.html',
  styleUrls: ['./calendly.component.scss'],
})
export class CalendlyComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() hideEventDetails: boolean = true;
  @Input() url: string = '';

  /**
   * Skips the inline embed and shows the "open the scheduler" card instead.  Set this if
   * Calendly ever stops rendering inside the native webview altogether.
   */
  @Input() forceExternal: boolean = false;

  /** How long to wait for the iframe before offering the external browser instead. */
  @Input() loadTimeoutMs: number = 10000;

  embedUrl: SafeResourceUrl | null = null;
  loaded = false;
  failed = false;

  /**
   * Calendly needs a real domain here.  Inside Cordova the page is served from
   * `ionic://localhost` / `http://localhost`, so fall back to our own host instead of
   * handing Calendly a `localhost` embed_domain it can't make sense of.
   */
  private readonly fallbackEmbedDomain = 'awaken180weightloss.com';
  private timeoutHandle: any = null;
  private viewReady = false;

  constructor(private sanitizer: DomSanitizer,
              private platform: Platform,
              private iab: InAppBrowser) { }

  /** True on a real device build, where a blank third-party iframe is a real possibility. */
  get isNative(): boolean {
    return this.platform.is('cordova') && !this.platform.is('desktop');
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewReady && (changes.url || changes.hideEventDetails || changes.forceExternal)) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.cancelTimeout();
  }

  /**
   * (Re)builds the inline widget.  Runs on every url change so the embed always reflects
   * the latest prefill params rather than whatever was bound when the component loaded.
   *
   * The iframe is built by hand rather than through Calendly's widget.js: that script is
   * loaded `async` from index.html, so in the native webview it is routinely still in
   * flight when this component initialises, and the resulting `Calendly is not defined`
   * left nothing but an empty div behind.
   */
  render(): void {
    this.cancelTimeout();
    this.loaded = false;
    this.failed = false;
    this.embedUrl = null;

    if (!this.url) return;

    if (this.forceExternal) {
      this.failed = true;
      return;
    }

    this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.buildUrl());

    // Nothing in the webview tells us the iframe rendered *blank*, so give it a deadline
    // and hand the user the browser if it never reports back.
    this.timeoutHandle = setTimeout(() => {
      if (!this.loaded) this.failed = true;
    }, this.loadTimeoutMs);
  }

  buildUrl(): string {
    const params = new URLSearchParams({
      embed_domain: this.embedDomain(),
      embed_type: 'Inline'
    });

    if (this.hideEventDetails) params.set('hide_event_type_details', '1');

    return this.url + (this.url.indexOf('?') >= 0 ? '&' : '?') + params.toString();
  }

  /** The raw scheduling link, for opening outside the embed. */
  externalUrl(): string {
    if (!this.hideEventDetails) return this.url;
    return this.url + (this.url.indexOf('?') >= 0 ? '&' : '?') + 'hide_event_type_details=1';
  }

  onFrameLoad(): void {
    this.loaded = true;
    this.cancelTimeout();
  }

  /**
   * Opens the scheduler outside the embed.  Native builds use the in-app browser, which
   * gets its own cookie jar and renders Calendly reliably; the web build just opens a tab.
   */
  openExternally(): void {
    const url = this.externalUrl();

    if (this.isNative) {
      this.iab.create(url, '_blank', { location: 'yes' });
      return;
    }

    window.open(url, '_blank');
  }

  private embedDomain(): string {
    const host = window.location.host;
    if (!host || host.indexOf('localhost') >= 0) return this.fallbackEmbedDomain;
    return host;
  }

  private cancelTimeout(): void {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }
}
